import React, { useContext, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FiChevronDown, FiChevronRight, FiRotateCw } from "react-icons/fi";
import moment from "moment";
import {
  listDeliveries,
  redeliver,
} from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";

const PAGE_LIMIT = 20;
const REPLAY_REFRESH_MS = 2500;

const formatTs = (ts) => (ts ? moment(ts).format("DD-MM-YYYY HH:mm:ss") : "—");

const STATUS_BADGE = {
  succeeded: "bg-success",
  pending: "bg-warning",
  failed: "bg-danger",
  dlq: "bg-dark",
};

const StatusBadge = ({ status }) => (
  <span className={`badge ${STATUS_BADGE[status] || "bg-secondary"}`}>
    {status}
  </span>
);

const prettyJson = (raw) => {
  if (raw == null || raw === "") return "";
  if (typeof raw !== "string") {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const preStyle = {
  background: "var(--bs-tertiary-bg, #f8f9fa)",
  border: "1px solid var(--bs-border-color, #dee2e6)",
  borderRadius: 4,
  padding: "8px 12px",
  margin: 0,
  fontSize: 12,
  lineHeight: 1.4,
  maxHeight: 400,
  overflowX: "auto",
  overflowY: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const DeliveryRow = ({ delivery, onReplay, replaying }) => {
  const [open, setOpen] = useState(false);
  const body = prettyJson(delivery.response_body);
  const headers = prettyJson(delivery.response_headers);
  const hasDetail = body || headers;
  return (
    <>
      <tr>
        <td style={{ width: 30 }}>
          {hasDetail ? (
            <button
              type="button"
              className="btn btn-link p-0 text-muted"
              onClick={() => setOpen((v) => !v)}
              title={open ? "Hide details" : "Show details"}
            >
              {open ? <FiChevronDown /> : <FiChevronRight />}
            </button>
          ) : null}
        </td>
        <td>
          <StatusBadge status={delivery.status} />
        </td>
        <td>{delivery.attempt}</td>
        <td>{delivery.http_status != null ? delivery.http_status : "—"}</td>
        <td>{delivery.duration_ms != null ? `${delivery.duration_ms}ms` : "—"}</td>
        <td>{formatTs(delivery.ts_attempted)}</td>
        <td>
          {delivery.status === "pending"
            ? formatTs(delivery.next_retry_at)
            : "—"}
        </td>
        <td>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => onReplay(delivery)}
            disabled={replaying}
            title="Replay this delivery"
          >
            <FiRotateCw className="align-middle me-1" />
            Replay
          </Button>
        </td>
      </tr>
      {open && hasDetail && (
        <tr>
          <td colSpan={8} className="bg-light">
            <div className="p-2">
              <div className="mb-2">
                <div className="text-muted small mb-1">
                  Delivery ID: <code>{delivery.delivery_id}</code>
                </div>
                <div className="text-muted small">
                  Event ID: <code>{delivery.idt_integration_app_event}</code>
                </div>
              </div>
              {body && (
                <div className="mb-2">
                  <div className="text-muted small mb-1">Response body</div>
                  <pre style={preStyle}>{body}</pre>
                </div>
              )}
              {headers && (
                <div className="mb-1">
                  <div className="text-muted small mb-1">Response headers</div>
                  <pre style={preStyle}>{headers}</pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const DeliveriesPanel = ({ endpointID, eventIDFilter }) => {
  const { showSuccess, showError } = useContext(ToastrContext);
  const [deliveries, setDeliveries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replaying, setReplaying] = useState(false);

  const load = async (after = null) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_LIMIT };
      if (after) params.starting_after = after;
      if (eventIDFilter) params.event_id = eventIDFilter;
      const res = await listDeliveries(endpointID, params);
      const items = Array.isArray(res.data) ? res.data : [];
      setDeliveries((prev) => (after ? [...prev, ...items] : items));
      setHasMore(!!res.has_more);
      setCursor(res.next_cursor || null);
    } catch (e) {
      showError((e && e.message) || "Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDeliveries([]);
    setCursor(null);
    setHasMore(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointID, eventIDFilter]);

  const refreshAll = async () => {
    setDeliveries([]);
    setCursor(null);
    setHasMore(false);
    await load();
  };

  const handleReplay = async (delivery) => {
    setReplaying(true);
    try {
      const res = await redeliver(endpointID, delivery.id);
      showSuccess(`Delivery queued (${(res && res.delivery_id) || "ok"})`);
      // Worker polls every 2s; give it a moment to attempt the redelivery.
      setTimeout(() => {
        refreshAll();
      }, REPLAY_REFRESH_MS);
    } catch (e) {
      showError((e && e.message) || "Failed to replay delivery");
    } finally {
      setReplaying(false);
    }
  };

  return (
    <div className="px-3 pb-3 pt-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <strong className="me-2">Deliveries</strong>
          <span className="text-muted small">
            {deliveries.length} loaded{hasMore ? "+" : ""}
          </span>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={refreshAll}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Loading...
            </>
          ) : (
            "Refresh"
          )}
        </Button>
      </div>

      {loading && deliveries.length === 0 ? (
        <div className="text-center p-4">
          <Spinner animation="border" />
        </div>
      ) : deliveries.length === 0 ? (
        <div className="text-center p-4 text-muted">
          <p className="mb-0">No deliveries yet.</p>
          <p className="small mb-0">
            Trigger an event for one of this endpoint's subscribed types — the
            dispatcher polls every ~2s.
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th></th>
                <th>Status</th>
                <th>Attempt</th>
                <th>HTTP</th>
                <th>Duration</th>
                <th>Attempted</th>
                <th>Next Retry</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <DeliveryRow
                  key={d.id}
                  delivery={d}
                  onReplay={handleReplay}
                  replaying={replaying}
                />
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div className="text-center mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => load(cursor)}
                disabled={loading}
              >
                {loading ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveriesPanel;
